import { motion } from 'framer-motion';
import { Quote, ArrowUpRight, ShieldCheck, Layers, Sun } from 'lucide-react';
import { COMPANY } from '../data/site';
import { useEnquiry } from '../context/EnquiryContext';

const PRINCIPLES = [
  { icon: ShieldCheck, title: 'Transparency first', desc: 'NA orders, EC and layout plans shown before you are asked. Documentation you can verify independently.' },
  { icon: Layers, title: 'One integrated partner', desc: 'Land development, construction and material supply under one roof — fewer middlemen, tighter quality control.' },
  { icon: Sun, title: 'Built for tomorrow', desc: 'Avenue plantations, water security and a solar division in the making — every project planned a decade ahead.' },
];

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-60px' },
  transition: { duration: 0.7, delay },
});

export default function AboutDeveloper() {
  const { open } = useEnquiry();
  return (
    <main data-testid="about-developer-page">
      <section className="pt-40 pb-20 lg:pb-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <p className="text-[10px] tracking-[0.3em] text-terra font-medium mb-6">ABOUT THE DEVELOPER</p>
          <motion.h1
            initial={{ opacity: 0, y: 34 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="font-heading font-extrabold tracking-tighter leading-[0.95] text-5xl sm:text-7xl text-ink max-w-4xl"
            data-testid="developer-headline"
          >
            {COMPANY.owner}
          </motion.h1>
          <motion.p {...fade(0.15)} className="mt-6 text-[11px] tracking-[0.24em] text-ink/50 font-medium">
            FOUNDER & MANAGING DIRECTOR — {COMPANY.name.toUpperCase()}
          </motion.p>
          <motion.p {...fade(0.2)} className="mt-8 text-base lg:text-lg text-ink/60 leading-relaxed max-w-3xl" data-testid="developer-story">
            A developer with a builder's hands and a farmer's patience. {COMPANY.owner} founded {COMPANY.shortName}
            on a simple conviction: families deserve land they can verify, infrastructure they can trust, and a
            developer who answers the phone after the sale is done. From plotted layouts to construction and
            material supply, his team keeps every link of the chain in-house — so nothing is lost between promises.
          </motion.p>
        </div>
      </section>

      <section className="py-20 lg:py-24 bg-stone border-y border-clay" data-testid="developer-principles">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <p className="text-[10px] tracking-[0.3em] text-terra font-medium mb-4">WORKING PRINCIPLES</p>
          <h2 className="font-heading font-extrabold tracking-tighter text-3xl sm:text-4xl text-ink mb-14">How he builds</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-clay border border-clay">
            {PRINCIPLES.map((p, i) => (
              <motion.div key={p.title} {...fade(i * 0.08)} className="bg-sand p-8 lg:p-10" data-testid={`principle-${i}`}>
                <span className="w-11 h-11 bg-terra/10 text-terra flex items-center justify-center"><p.icon size={18} /></span>
                <h3 className="mt-6 font-heading font-bold text-xl tracking-tight text-ink">{p.title}</h3>
                <p className="mt-3 text-sm text-ink/60 leading-relaxed">{p.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28" data-testid="developer-quote">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-4">
            <div className="aspect-[4/5] bg-stone border border-clay flex items-center justify-center">
              <span className="font-serif italic text-8xl text-terra/40">RR</span>
            </div>
          </div>
          <div className="lg:col-span-8">
            <Quote size={36} className="text-terra/30 mb-6" />
            <motion.blockquote {...fade(0)} className="font-serif italic text-2xl sm:text-3xl lg:text-4xl leading-snug text-ink max-w-2xl">
              “Every plot we layout and every wall we raise must outlive us. That is the only way to lead a better future.”
            </motion.blockquote>
            <motion.p {...fade(0.1)} className="mt-8 font-serif italic text-lg text-terra">
              {COMPANY.tagline}
            </motion.p>
            <motion.p {...fade(0.12)} className="mt-6 text-xs text-ink/40 tracking-[0.18em]">
              FOCUS REGIONS — KARNATAKA · TELANGANA · ANDHRA PRADESH · MAHARASHTRA
            </motion.p>
            <motion.button {...fade(0.15)} onClick={() => open()} data-testid="developer-enquiry-btn"
              className="mt-10 bg-moss text-sand text-[11px] font-medium tracking-[0.2em] px-10 py-4 flex items-center gap-2 hover:bg-terra transition-colors duration-300">
              TALK TO THE TEAM <ArrowUpRight size={14} />
            </motion.button>
          </div>
        </div>
      </section>
    </main>
  );
}
